// Utility functions for color manipulation

/**
 * Convert hex color to rgba with opacity
 * @param {string} hex - Hex color (e.g., '#006EC2')
 * @param {number} opacity - Opacity value between 0 and 1
 * @returns {string} - RGBA color string
 */
export const hexToRgba = (hex, opacity = 1) => {
  // Remove # if present
  hex = hex.replace('#', '');
  
  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Lighten a color by a percentage
 * @param {string} hex - Hex color
 * @param {number} percent - Percentage to lighten (0-100)
 * @returns {string} - Lightened hex color
 */
export const lightenColor = (hex, percent) => {
  hex = hex.replace('#', '');
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  const newR = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  const newG = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  const newB = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Darken a color by a percentage
 * @param {string} hex - Hex color
 * @param {number} percent - Percentage to darken (0-100)
 * @returns {string} - Darkened hex color
 */
export const darkenColor = (hex, percent) => {
  hex = hex.replace('#', '');
  
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
  const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
  const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Get hover color based on primary/secondary colors
 * @param {string} primaryColor - Primary color
 * @param {string} secondaryColor - Secondary color
 * @param {string} type - 'primary' or 'secondary'
 * @returns {string} - Hover color
 */
export const getHoverColor = (primaryColor, secondaryColor, type = 'primary') => {
  const baseColor = type === 'primary' ? primaryColor : secondaryColor;
  return lightenColor(baseColor, 20);
};

/**
 * Get active background color with transparency
 * @param {string} color - Base color
 * @param {number} opacity - Opacity (0-1)
 * @returns {string} - Background color with transparency
 */
export const getActiveBackgroundColor = (color, opacity = 0.15) => {
  return hexToRgba(color, opacity);
};

/**
 * Get appropriate text color based on background color
 * @param {string} backgroundColor - Background color (hex)
 * @returns {string} - Text color (white or black)
 */
export const getContrastTextColor = (backgroundColor) => {
  // Remove # if present
  const hex = backgroundColor.replace('#', '');
  
  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return white for dark backgrounds, black for light backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/**
 * Get text color for primary color background
 * @param {string} primaryColor - Primary color
 * @returns {string} - Contrasting text color
 */
export const getPrimaryTextColor = (primaryColor) => {
  return getContrastTextColor(primaryColor);
};

/**
 * Get text color for secondary color background
 * @param {string} secondaryColor - Secondary color
 * @returns {string} - Contrasting text color
 */
export const getSecondaryTextColor = (secondaryColor) => {
  return getContrastTextColor(secondaryColor);
};
